// The master spell list, from D&D Beyond: the spells the signed-in account is
// entitled to see (what its spell search shows — SRD, official and any
// third-party books it owns), read per class through the connector's effects
// lane (`ddbSpells`) and merged by id. Kept in the layout (compact rows) so
// the Spell art page and the effects engine list and draw them without the
// DM Screen's own inventory as a gate; the inventory stays the fallback for
// handlers and for a spell D&D Beyond does not return.
import { newRequestId, request } from "../bridge";
import type { Character } from "./types";
import type { Layout } from "./types";
import { normSpellName as norm } from "./text";

export interface DdbSpell { id: number; name: string; level: number; school: string; concentration: boolean; ritual: boolean; legacy?: boolean; duration: { interval: number; unit: string; type: string } | null; range: { origin: string; value: number } | null; aoe: { type: string; size: number } | null; save: string; attack: boolean; damage: { type: string; dice: string; scaling: string }[]; conditions: number[]; sources: number[]; tags: string[] }
export interface DdbCatalogue { at: string; classes: number[]; spells: DdbSpell[] }

/** D&D Beyond class ids for the spell lists (every caster, so the union is the whole list the account can see). */
export const DDB_CLASS_IDS: { id: number; name: string }[] = [{ id: 1, name: "Bard" }, { id: 2, name: "Cleric" }, { id: 3, name: "Druid" }, { id: 4, name: "Paladin" }, { id: 5, name: "Ranger" }, { id: 6, name: "Sorcerer" }, { id: 7, name: "Warlock" }, { id: 8, name: "Wizard" }, { id: 252, name: "Artificer" }];

export const ddbCatalogueOf = (layout: Layout): DdbCatalogue | null => { const c = layout.ddbSpellCatalogue as DdbCatalogue | undefined; return c && Array.isArray(c.spells) ? c : null; };
export const ddbCatalogueStale = (c: DdbCatalogue | null, days = 7) => !c || Date.now() - Date.parse(c.at) > days * 86_400_000;

/* THE SINGLE-TARGET RETICLE'S SILENT PREREQUISITE.
   targetSpellFor reads a spell's range, damage and save ONLY from this catalogue and
   has no static fallback, so with it unloaded every single-target cast is dropped with
   no sign. The area engine never hit this because effectDefFor also matches LEVERS and
   spellInfo — which is why "place on cast" worked at the table while the reticle did
   nothing. The catalogue otherwise loads only when the Spell art page is opened by hand.
   So when the DM has turned single-target markers on and is at a table with a connector
   that can read the lists, load it once if the cached copy is missing or a week stale.
   Pure, so the decision is tested without a bridge. */
export function shouldLoadCatalogue(opts: { connected: boolean; canReadLists: boolean; wantsSingleTarget: boolean; hasLink: boolean; catalogue: DdbCatalogue | null }): boolean {
  return opts.connected && opts.canReadLists && opts.wantsSingleTarget && opts.hasLink && ddbCatalogueStale(opts.catalogue);
}

/** Read every class list and merge by spell id. Reports progress per class. */
export async function loadDdbCatalogue(onProgress?: (done: number, total: number, cls: string) => void): Promise<DdbCatalogue> {
  const byId = new Map<number, DdbSpell>();
  const classes: number[] = [];
  let done = 0;
  for (const cls of DDB_CLASS_IDS) {
    const r = await request<{ ok: boolean; error?: string; spells?: DdbSpell[] }>({ type: "DM_SCREEN_LMC_EFFECTS_REQUEST", requestId: newRequestId("ddb-spells"), op: { op: "ddbSpells", classId: cls.id, classLevel: 20 } }, { resultType: "DM_SCREEN_LMC_EFFECTS_RESULT", timeoutMs: 30000 });
    if (!r.ok) throw new Error(r.error || `D&D Beyond did not answer for ${cls.name}`);
    for (const s of r.spells || []) if (!byId.has(s.id)) byId.set(s.id, s);
    classes.push(cls.id); done += 1; onProgress?.(done, DDB_CLASS_IDS.length, cls.name);
  }
  return { at: new Date().toISOString(), classes, spells: Array.from(byId.values()).sort((a, b) => a.level - b.level || a.name.localeCompare(b.name)) };
}

/** A spell by name from the catalogue (2014 suffixes ignored). Both editions come back under
 *  one name (D&D Beyond marks the 2014 entry legacy): the edition asked for wins, else whichever exists. */
export function ddbSpell(c: DdbCatalogue | null, name: string, edition = "2024"): DdbSpell | null {
  if (!c) return null; const n = norm(name);
  const all = c.spells.filter((s) => norm(s.name) === n); if (!all.length) return null;
  const want = edition === "2014";
  return all.find((s) => Boolean(s.legacy) === want) || all[0];
}
/** The catalogue's area spells (an area of effect D&D Beyond states), by name. */
export const ddbAreaSpells = (c: DdbCatalogue | null): DdbSpell[] => (c ? c.spells.filter((s) => s.aoe && s.aoe.size > 0) : []);
/** The spell text this account can see, for a template's GM Notes.
 *  A miss is not an error: D&D Beyond only returns what the account owns, so
 *  "not found" IS "you do not have this one" and the caller writes a link
 *  instead. The prose is deliberately NOT part of DdbCatalogue — that lives in
 *  the layout, which is persisted, and 1,168 descriptions would bloat every
 *  save of it. The connector keeps them for the session and no longer.
 *  The timeout is generous because a cold connector cache warms itself from
 *  every caster class before it can answer the first question. */
export async function ddbSpellText(name: string, edition = "2024"): Promise<{ owned: boolean; name?: string; id?: number; description?: string }> {
  const r = await request<{ ok: boolean; error?: string; owned?: boolean; name?: string; id?: number; description?: string }>(
    { type: "DM_SCREEN_LMC_EFFECTS_REQUEST", requestId: newRequestId("ddb-spell-text"), op: { op: "ddbSpellText", name, edition } },
    { resultType: "DM_SCREEN_LMC_EFFECTS_RESULT", timeoutMs: 60000 });
  if (!r.ok) return { owned: false, name };
  return { owned: Boolean(r.owned), name: r.name || name, id: r.id, description: r.description };
}

/** Duration in rounds for the engine's clock (a minute is 10; instantaneous is null). */
export function ddbRounds(s: DdbSpell): number | null {
  if (!s.duration || /instant/i.test(s.duration.type || "")) return null;
  const n = Number(s.duration.interval) || 0; const u = String(s.duration.unit || "").toLowerCase();
  if (/round/.test(u)) return n; if (/minute/.test(u)) return n * 10; if (/hour/.test(u)) return n * 600; if (/day/.test(u)) return n * 14400;
  return n || null;
}

/* THE CATALOGUE, PLUS WHAT THE PARTY ACTUALLY CARRIES.

   D&D Beyond's class-list endpoint returns what a character KNOWS. A Cleric, Druid,
   Paladin or Artificer knows only its cantrips — the levelled spells are PREPARED
   from the full list — so the catalogue is missing the whole levelled cleric list,
   and Paladin comes back empty because paladins have no cantrips. Measured against
   the DM's own account on 2026-09-29: Bard 428 spells across levels 0-9, Cleric 23
   and all of them cantrips.

   A character's spellbook carries the same definition fields, so the spells the
   party can actually cast fill the gap. That is a narrower set than the true class
   list and deliberately so: it is exactly the spells that will be cast at this
   table, which is what the target picker and the effects engine need.

   The catalogue WINS on a name clash. It is the class list as D&D Beyond publishes
   it; a spellbook entry may be a character's legacy copy of the same spell. */
/* THE PARTY'S BOOKS ARE THE MASTER LIST; the class route is the bonus.
 *
 * Diagnosed in S1-7h and worth restating because it is counter-intuitive:
 * game-data/spells?classId returns what a character KNOWS, so a prepared caster
 * returns cantrips only -- Cleric 23, Druid 43, Paladin ZERO. The table only ever
 * casts what somebody has prepared, so the union of the party's own spellbooks is
 * the more complete source, and the class sweep is what fills in the spells
 * nobody happens to have prepared today.
 *
 * `requireDefinition` is the difference between the two kinds of caller:
 *
 *   TRUE (the default, for the effects engine) -- a spell whose definition did
 *   not arrive is skipped rather than admitted with no reach, because a template
 *   with a radius of zero looks like a bug at the table.
 *
 *   FALSE (for a reading or search list) -- include it anyway. Dropping Bless
 *   from a search because its range did not arrive is the worse failure: the DM
 *   asked what it says, not where it reaches.
 */
export function catalogueWithSpellbooks(
  catalogue: DdbCatalogue | null,
  characters: { spellbook?: NonNullable<Character["spellbook"]> }[],
  opts?: { requireDefinition?: boolean },
): DdbCatalogue | null {
  const requireDefinition = opts?.requireDefinition !== false;
  const extra: DdbSpell[] = [];
  const have = new Set((catalogue?.spells || []).map((s) => s.name.toLowerCase()));
  for (const c of characters) for (const b of c.spellbook || []) {
    const key = String(b.name || "").toLowerCase();
    if (!key || have.has(key)) continue;
    /* No range means the connector predates 0.8.203 and threw the definition away.
       Skipped rather than admitted as a spell with no reach, which would give the
       picker a circle of zero and look like a bug at the table. */
    if (requireDefinition && !b.range) continue;
    have.add(key);
    extra.push({ id: Number(b.id) || 0, name: String(b.name), level: Number(b.level) || 0, school: "",
      concentration: Boolean(b.concentration), ritual: Boolean(b.ritual), legacy: Boolean(b.legacy),
      duration: null, range: b.range || null, aoe: b.aoe || null, save: String(b.save || ""),
      attack: Boolean(b.attack), damage: b.damage || [], conditions: [], sources: [], tags: b.tags || [] });
  }
  if (!extra.length) return catalogue;
  const at = catalogue?.at || new Date().toISOString();
  return { at, classes: catalogue?.classes || [], spells: [...(catalogue?.spells || []), ...extra].sort((a, b) => a.level - b.level || a.name.localeCompare(b.name)) };
}

/** The master spell list: the class sweep plus every spell anybody has prepared.
 *
 *  Every consumer should use this rather than ddbCatalogueOf. Two did not, and
 *  both were missing exactly the prepared spells a prepared caster has -- which
 *  is to say most of what the table actually casts.
 *
 *  Not campaign-scoped, deliberately, and for the same reason EffectsHost is not:
 *  this is a list of spell DEFINITIONS, not of who is here. Dropping Bless
 *  because the character who knows it plays in the other campaign would make a
 *  reference list lie about what a spell is. Who can CAST it is a separate
 *  question, asked of the scoped roster. */
export function masterSpellList(
  layout: Layout,
  opts?: { requireDefinition?: boolean },
): DdbCatalogue | null {
  return catalogueWithSpellbooks(ddbCatalogueOf(layout), layout.characters || [], opts);
}

/** Who in this party has a given spell prepared. The question a DM asks out
 *  loud, answered from records already in hand. Scoped, because this one IS
 *  about who is here. */
export function preparedBy(characters: { name: string; spellbook?: NonNullable<Character["spellbook"]> }[], spellName: string): string[] {
  const want = String(spellName || "").trim().toLowerCase();
  if (!want) return [];
  return characters
    .filter((c) => (c.spellbook || []).some((b) => b.prepared && String(b.name || "").trim().toLowerCase() === want))
    .map((c) => c.name);
}